import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// One reusable editor for all 4 fixed legal pages, distinguished by :slug
// in the URL (privacy-policy / terms-conditions / refund-policy / cookie-policy).
const LegalPageForm = () => {
    const { slug } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { user } = useSelector((state) => state.user);
    const canEdit = user.role === "admin" || user.adminRole?.permissions?.pages?.edit;
    const [title, setTitle] = useState("");
    const [titleZh, setTitleZh] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [bodyHtmlZh, setBodyHtmlZh] = useState("");

    useEffect(() => {
        const fetchPage = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`/api/v1/legal-page/${slug}`);
                setTitle(data.page.title);
                setTitleZh(data.page.titleZh || "");
                setBodyHtml(data.page.bodyHtml);
                setBodyHtmlZh(data.page.bodyHtmlZh || "");
            } catch (error) {
                enqueueSnackbar("Failed to load page", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchPage();
        // eslint-disable-next-line
    }, [slug]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`/api/v1/admin/legal-page/${slug}`, { title, titleZh, bodyHtml, bodyHtmlZh });
            enqueueSnackbar("Page updated", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: Edit ${title} | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <div className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6">
                <h1 className="text-lg font-medium uppercase">Edit {title}</h1>

                <TextField label="Page Title" size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
                <ReactQuill theme="snow" value={bodyHtml} onChange={setBodyHtml} style={{ height: 350, marginBottom: 50 }} />

                <TextField label="Page Title (中文 — optional)" size="small" value={titleZh} onChange={(e) => setTitleZh(e.target.value)} />
                <ReactQuill theme="snow" value={bodyHtmlZh} onChange={setBodyHtmlZh} style={{ height: 350, marginBottom: 50 }} />

                {canEdit && (
                    <div className="flex justify-end">
                        <button onClick={handleSave} className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg">Save Changes</button>
                    </div>
                )}
            </div>
        </>
    );
};

export default LegalPageForm;
